import { Modal } from "../../utils/Modal";

/**
 * CMS 메뉴 항목 폼 — 링크 유형(select[name="link_type"]) 값에 따라
 * 폼 루트(.cms-item-form[data-cms-item-form])에 data-link-type 을 설정한다.
 * 영역 노출 토글은 SCSS(.cms-item-form__when[data-link~=...])가 담당.
 *
 * '파일 연결'(view) 유형:
 *  - view_path(= resources/views/ 기준 상대경로, name="view_path")가 단일 소스.
 *  - 입력 시 서버(admin.site.cms.view-files)에서 일치하는 실제 뷰 파일을 받아 드롭다운으로 표시·선택.
 *  - 공개 URL 은 view_path 에서 즉시 도출해 미리보기(서버 urlFromViewPath 와 동일 규칙).
 *
 * select 는 SlimSelect 로 래핑돼도 원본 select 에 change 가 발생하므로 그 이벤트만 구독하면 안전.
 * initAdminFormControls 에서 호출 → 모달·인스펙터 두 경로 모두 자동 적용. 멱등(가드).
 */
export function initCmsItemForm(scope: Document | HTMLElement = document): void {
    const list =
        scope instanceof HTMLElement && scope.matches?.("[data-cms-item-form]")
            ? [scope]
            : Array.from(
                  (scope as ParentNode).querySelectorAll<HTMLElement>(
                      "[data-cms-item-form]",
                  ),
              );

    list.forEach((form) => {
        if (form.dataset.linkSwitchInited === "1") return;
        const sel = form.querySelector<HTMLSelectElement>(
            'select[name="link_type"]',
        );
        if (!sel) return;
        form.dataset.linkSwitchInited = "1";
        const apply = (): void => {
            const linkType = sel.value || "view";
            form.dataset.linkType = linkType;
            applyConditionalRequired(form, linkType);
        };
        apply();
        sel.addEventListener("change", apply);

        initViewFilePicker(form);
        initRedirectPicker(form);
    });
}

function applyConditionalRequired(form: HTMLElement, linkType: string): void {
    form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
        "[data-link-required]",
    ).forEach((field) => {
        const requiredFor = (field.dataset.linkRequired || "")
            .split(/\s+/)
            .filter(Boolean);
        const shouldRequire = requiredFor.includes(linkType);
        field.required = shouldRequire;
        if (shouldRequire) {
            field.setAttribute("aria-required", "true");
        } else {
            field.removeAttribute("aria-required");
        }
    });
}

/** PHP MenuViewPathService::urlFromViewPath 와 동일 규칙으로 view_path → 공개 URL. */
function urlFromViewPath(raw: string): string {
    let vp = raw
        .trim()
        .replace(/\\/g, "/")
        .replace(/^resources\/views\/?/i, "")
        .replace(/\.php$/i, "")
        .replace(/^\/+|\/+$/g, "");
    if (vp.startsWith("pages/")) vp = vp.slice(6);
    else if (vp === "pages") vp = "";
    if (vp === "index") return "/";
    if (vp.endsWith("/index")) vp = vp.slice(0, -6);
    return "/" + vp;
}

/**
 * 파일 연결 입력 위젯 — 자동완성 드롭다운 + URL 미리보기.
 */
function initViewFilePicker(form: HTMLElement): void {
    const root = form.querySelector<HTMLElement>("[data-cms-viewfile]");
    if (!root || root.dataset.inited === "1") return;
    root.dataset.inited = "1";

    const input = root.querySelector<HTMLInputElement>("[data-cms-viewfile-input]");
    const dropdown = root.querySelector<HTMLElement>("[data-cms-viewfile-dropdown]");
    const urlOut = root.querySelector<HTMLElement>("[data-cms-viewfile-url]");
    const listUrl = root.dataset.listUrl || "";
    if (!input || !dropdown) return;

    const renderUrl = (): void => {
        if (urlOut) urlOut.textContent = urlFromViewPath(input.value || input.placeholder || "");
    };
    renderUrl();

    const closeDropdown = (): void => {
        dropdown.hidden = true;
        dropdown.innerHTML = "";
    };

    const pick = (value: string): void => {
        input.value = value;
        renderUrl();
        closeDropdown();
        input.focus();
    };

    const showFiles = (files: string[]): void => {
        dropdown.innerHTML = "";
        if (files.length === 0) {
            const li = document.createElement("li");
            li.className = "cms-viewfile__option cms-viewfile__option--empty";
            li.textContent = "일치하는 파일 없음 — 저장 시 새 파일이 생성됩니다.";
            dropdown.appendChild(li);
            dropdown.hidden = false;
            return;
        }
        files.forEach((file) => {
            const li = document.createElement("li");
            li.className = "cms-viewfile__option";
            li.textContent = file;
            li.addEventListener("mousedown", (e) => {
                e.preventDefault();
                pick(file);
            });
            dropdown.appendChild(li);
        });
        dropdown.hidden = false;
    };

    let timer: number | undefined;
    const fetchFiles = (): void => {
        if (!listUrl) return;
        const q = input.value.trim();
        window.clearTimeout(timer);
        timer = window.setTimeout(() => {
            const url = listUrl + (listUrl.includes("?") ? "&" : "?") + "q=" + encodeURIComponent(q);
            fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest", Accept: "application/json" } })
                .then((r) => r.json())
                .then((data: { files?: string[] }) => {
                    showFiles(Array.isArray(data?.files) ? data.files : []);
                })
                .catch(() => closeDropdown());
        }, 180);
    };

    input.addEventListener("input", () => {
        renderUrl();
        fetchFiles();
    });
    input.addEventListener("focus", fetchFiles);
    input.addEventListener("blur", () => {
        // mousedown 으로 선택을 먼저 처리하도록 약간 지연 후 닫기.
        window.setTimeout(closeDropdown, 120);
    });
}

/**
 * 리다이렉트 대상 메뉴 선택 — hidden redirect_item_id + Modal.content() 트리 피커.
 */
function initRedirectPicker(form: HTMLElement): void {
    const roots = Array.from(
        form.querySelectorAll<HTMLElement>("[data-cms-redirect-picker]"),
    );
    roots.forEach((root) => {
        if (root.dataset.inited === "1") return;
        root.dataset.inited = "1";

        const trigger = root.querySelector<HTMLElement>("[data-cms-redirect-trigger]");
        const clear = root.querySelector<HTMLButtonElement>("[data-cms-redirect-clear]");
        const input = root.querySelector<HTMLInputElement>("[data-cms-redirect-input]");
        const current = root.querySelector<HTMLElement>("[data-cms-redirect-current]");
        const template = root.querySelector<HTMLTemplateElement>("[data-cms-redirect-template]");
        if (!trigger || !input || !current || !template) return;

        const placeholder = root.dataset.placeholder || "— 메뉴 항목 선택 —";
        const setSelection = (value: string, label: string): void => {
            input.value = value;
            input.dispatchEvent(new Event("change", { bubbles: true }));
            current.textContent = value !== "" ? label : placeholder;
            root.classList.toggle("has-selection", value !== "");
            root.dataset.selectedValue = value;
        };
        root.classList.toggle("has-selection", input.value !== "");
        root.dataset.selectedValue = input.value;

        trigger.addEventListener("click", () => {
            openRedirectPicker(root, trigger, template, setSelection);
        });

        clear?.addEventListener("click", () => {
            setSelection("", "");
            trigger.focus();
        });
    });
}

function openRedirectPicker(
    root: HTMLElement,
    trigger: HTMLElement,
    template: HTMLTemplateElement,
    setSelection: (value: string, label: string) => void,
): void {
    const title = trigger.getAttribute("data-picker-title") ?? "리다이렉트 대상 메뉴";
    Modal.content(template.innerHTML, title, "wide").catch(() => {});

    const panel = document.querySelector<HTMLElement>(
        ".app-modal-body [data-cms-redirect-picker-panel]",
    );
    if (!panel) return;

    bindRedirectPickerPanel(panel, root, trigger, setSelection);
}

function bindRedirectPickerPanel(
    panel: HTMLElement,
    root: HTMLElement,
    trigger: HTMLElement,
    setSelection: (value: string, label: string) => void,
): void {
    const search = panel.querySelector<HTMLInputElement>("[data-cms-redirect-search]");
    const nodes = Array.from(panel.querySelectorAll<HTMLElement>("[data-cms-redirect-node]"));
    const noResult = panel.querySelector<HTMLElement>("[data-cms-redirect-no-result]");

    panel.addEventListener("click", (event) => {
        const option = (event.target as HTMLElement).closest<HTMLButtonElement>(
            "[data-cms-redirect-option]",
        );
        if (!option || option.disabled) return;
        const value = option.dataset.value ?? "";
        const label = option.dataset.label ?? "";
        setSelection(value, label);
        document
            .querySelector<HTMLButtonElement>("#app-modal-root [data-app-modal-ok]")
            ?.click();
        window.setTimeout(() => trigger.focus(), 0);
    });

    if (!search) return;

    const rootNodes = nodes.filter((node) => {
        const parentNode = node.parentElement?.closest<HTMLElement>("[data-cms-redirect-node]");
        return parentNode === null;
    });

    const childNodes = (node: HTMLElement): HTMLElement[] => {
        const children = node.querySelector<HTMLElement>(".cms-redirect-picker__children");
        if (!children) return [];
        return Array.from(children.children).filter(
            (child): child is HTMLElement =>
                child instanceof HTMLElement && child.matches("[data-cms-redirect-node]"),
        );
    };

    const applyNode = (node: HTMLElement, query: string): boolean => {
        const option = node.querySelector<HTMLElement>("[data-cms-redirect-option]");
        const ownText = option?.dataset.cmsRedirectSearchText ?? "";
        const ownMatch = query === "" || ownText.includes(query);
        let childMatch = false;
        childNodes(node).forEach((child) => {
            childMatch = applyNode(child, query) || childMatch;
        });
        const visible = ownMatch || childMatch;
        node.hidden = !visible;
        return visible;
    };

    const applySearch = (): void => {
        const query = search.value.trim().toLowerCase();
        let visible = 0;
        rootNodes.forEach((node) => {
            if (applyNode(node, query)) visible++;
        });
        noResult?.toggleAttribute("hidden", visible !== 0);
    };

    search.addEventListener("input", applySearch);
    window.setTimeout(() => search.focus(), 50);
}
